import React, { useState } from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"

const DetailRow = ({ label, value }) => (
  <div className="admin-view-request__row">
    <span className="admin-view-request__row-label">{label}</span>
    <span className="admin-view-request__row-value">{value || "--"}</span>
  </div>
)

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

const REQUEST_SUBTITLE = {
  collaborator_limits: "Collaborator limit increase request",
  change_city: "City change request",
  join_platform: "Invitation request",
  photographers: "Photography collaborator request",
  reviewers: "Review writer request",
}

/**
 * Request detail / accept-reject modal — Figma
 * Invitation (`40006588:66828`) + Collaborator detail (`40006717:77470`).
 */
const ViewRequestModal = ({
  isOpen,
  request,
  onClose,
  onAccept,
  onDismiss,
  allowDecision = true,
}) => {
  const [isAccepting, setIsAccepting] = useState(false)

  if (!request) return null

  const type = request.requestType
  const isCollaboratorLimits = type === "collaborator_limits"
  const isChangeCity = type === "change_city"
  const isPhotographers = type === "photographers"
  const isReviewers = type === "reviewers"
  const isJoinPlatform = type === "join_platform"
  const canDecide =
    allowDecision && (request.status === "pending" || !request.status)
  const displayName = request.name || "this user"

  const handleAccept = async () => {
    if (!request?.id || isAccepting || !onAccept) return

    setIsAccepting(true)
    try {
      await onAccept(request)
    } finally {
      setIsAccepting(false)
    }
  }

  const handleDismiss = () => {
    if (!request?.id || isAccepting || !onDismiss) return
    onDismiss(request)
  }

  const title = isJoinPlatform ? "Invitation Request" : "Request Detail"
  const description = isJoinPlatform
    ? `${displayName} has submitted a request to join the platform. Review the details below:`
    : REQUEST_SUBTITLE[type] || `Review the request from ${displayName}.`
  const acceptLabel = isJoinPlatform ? "Accept Invitation" : "Approve"
  const areasLabel = Array.isArray(request.areasOfInterest)
    ? request.areasOfInterest.join(", ")
    : ""

  const renderDetailFields = () => {
    if (isJoinPlatform) {
      return (
        <>
          <DetailRow label="First Name" value={request.firstName} />
          <DetailRow label="Last Name" value={request.lastName} />
          <DetailRow label="Email" value={request.email} />
          <DetailRow label="City" value={request.city} />
          <DetailRow label="Reason" value={request.reason} />
        </>
      )
    }

    if (isCollaboratorLimits) {
      return (
        <>
          <DetailRow label="Project Name" value={request.projectName} />
          <DetailRow label="Current Limit" value={request.currentLimit} />
          <DetailRow label="Requested Limit" value={request.requestedLimit} />
          {request.approvedCount ? (
            <DetailRow label="Approved Limit" value={request.approvedCount} />
          ) : null}
          <DetailRow label="Date Submitted" value={request.date} />
          <div className="admin-view-request__row">
            <span className="admin-view-request__row-label">Status</span>
            <span
              className={`admin-view-request__status admin-view-request__status--${
                request.statusTone || "open"
              }`}
            >
              {request.statusLabel || "--"}
            </span>
          </div>
          <DetailRow label="Reason" value={request.reason} />
        </>
      )
    }

    if (isChangeCity) {
      return (
        <>
          <DetailRow label="First Name" value={request.firstName} />
          <DetailRow label="Last Name" value={request.lastName} />
          <DetailRow label="Email" value={request.email} />
          <DetailRow label="Current City" value={request.city} />
          <DetailRow label="Requested City" value={request.newCity} />
          <DetailRow label="Date Submitted" value={request.date} />
          <div className="admin-view-request__row">
            <span className="admin-view-request__row-label">Status</span>
            <span
              className={`admin-view-request__status admin-view-request__status--${
                request.statusTone || "open"
              }`}
            >
              {request.statusLabel || "--"}
            </span>
          </div>
          <DetailRow label="Reason" value={request.reason} />
        </>
      )
    }

    if (isPhotographers) {
      return (
        <>
          <DetailRow label="Subject" value={request.subject} />
          {request.email ? (
            <DetailRow label="Email" value={request.email} />
          ) : null}
          <DetailRow label="City" value={request.city} />
          {request.portfolioLink ? (
            <DetailRow
              label="Portfolio"
              value={
                <a
                  href={request.portfolioLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {request.portfolioLink}
                </a>
              }
            />
          ) : null}
          <DetailRow label="Date Submitted" value={request.date} />
          <div className="admin-view-request__row">
            <span className="admin-view-request__row-label">Status</span>
            <span
              className={`admin-view-request__status admin-view-request__status--${
                request.statusTone || "open"
              }`}
            >
              {request.statusLabel || "--"}
            </span>
          </div>
          <DetailRow label="Message" value={request.reason} />
        </>
      )
    }

    if (isReviewers) {
      return (
        <>
          <DetailRow label="First Name" value={request.firstName} />
          <DetailRow label="Last Name" value={request.lastName} />
          <DetailRow label="Email" value={request.email} />
          <DetailRow
            label="What to review first"
            value={request.whatToReviewFirst}
          />
          <DetailRow
            label="Why write reviews"
            value={request.whyWriteReviews}
          />
          {areasLabel ? (
            <DetailRow label="Areas of interest" value={areasLabel} />
          ) : null}
          <DetailRow label="Date Submitted" value={request.date} />
          <div className="admin-view-request__row">
            <span className="admin-view-request__row-label">Status</span>
            <span
              className={`admin-view-request__status admin-view-request__status--${
                request.statusTone || "open"
              }`}
            >
              {request.statusLabel || "--"}
            </span>
          </div>
        </>
      )
    }

    return (
      <>
        <DetailRow label="First Name" value={request.firstName} />
        <DetailRow label="Last Name" value={request.lastName} />
        <DetailRow label="Email" value={request.email} />
        <DetailRow label="City" value={request.city} />
        <DetailRow label="Reason" value={request.reason} />
      </>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={isAccepting ? undefined : onClose}
      centered
      className={`amok-modal amok-modal--admin-view-request${
        isJoinPlatform ? " amok-modal--admin-view-request--invite" : ""
      }`}
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-view-request__body">
        <div className="admin-view-request">
          <div className="admin-view-request__main">
            <div className="admin-view-request__header">
              <h3 className="admin-view-request__title">{title}</h3>
              <p className="admin-view-request__description">{description}</p>
            </div>

            {!isJoinPlatform ? (
              <div className="admin-view-request__identity">
                <div className="admin-view-request__identity-user">
                  <Avatar
                    src={request.avatar}
                    name={request.name}
                    size="sm"
                    className="admin-view-request__avatar"
                  />
                  <span className="admin-view-request__identity-name">
                    {request.name || "--"}
                  </span>
                </div>
                {request.city && request.city !== "--" ? (
                  <span className="admin-view-request__city-badge">
                    {isChangeCity
                      ? request.cityChangeLabel || request.city
                      : request.city}
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className="admin-view-request__details">
              {renderDetailFields()}
              {request.handlerName ? (
                <DetailRow label="Action By" value={request.handlerName} />
              ) : null}
              {request.decisionReason ? (
                <DetailRow
                  label="Decision Reason"
                  value={request.decisionReason}
                />
              ) : null}
            </div>
          </div>

          <div
            className={`admin-view-request__actions${
              canDecide ? "" : " admin-view-request__actions--single"
            }`}
          >
            <CustomButton
              variant="secondary"
              size="md"
              type="button"
              className="admin-view-request__cancel"
              onClick={onClose}
              disabled={isAccepting}
            >
              Cancel
            </CustomButton>

            {canDecide ? (
              <div className="admin-view-request__actions-primary">
                {!isJoinPlatform ? (
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="admin-view-request__dismiss"
                    onClick={handleDismiss}
                    disabled={isAccepting}
                  >
                    Reject
                  </CustomButton>
                ) : null}
                <CustomButton
                  variant="primary"
                  size="md"
                  type="button"
                  className="admin-view-request__accept"
                  onClick={handleAccept}
                  loading={isAccepting}
                  disabled={isAccepting}
                >
                  {acceptLabel}
                </CustomButton>
              </div>
            ) : null}
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

ViewRequestModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  request: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAccept: PropTypes.func,
  onDismiss: PropTypes.func,
  allowDecision: PropTypes.bool,
}

export default ViewRequestModal
